import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import GridLayout, { WidthProvider, type LayoutItem } from 'react-grid-layout/legacy';
import { useDashboardStore } from '../state/dashboardStore';
import { CHART_TYPES } from '../lib/charts';
import type { WidgetLayout } from '../lib/dashboards';
import DashboardWidget from '../components/dashboard/DashboardWidget';
import DashboardFilterDrawer from '../components/dashboard/DashboardFilterDrawer';

const SizedGridLayout = WidthProvider(GridLayout);

const GRID_COLS = 12;
const GRID_ROW_HEIGHT = 48;
const LAYOUT_DEBOUNCE_MS = 250;
const CREATED_TOAST_MS = 3000;

export default function DashboardEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const justCreated =
    (location.state as { justCreated?: boolean } | null)?.justCreated === true;

  const dashboard = useDashboardStore((s) =>
    id ? s.dashboards.find((d) => d.id === id) ?? null : null,
  );
  const renameDashboard = useDashboardStore((s) => s.renameDashboard);
  const deleteDashboard = useDashboardStore((s) => s.deleteDashboard);
  const addWidget = useDashboardStore((s) => s.addWidget);
  const updateLayouts = useDashboardStore((s) => s.updateLayouts);

  useEffect(() => {
    if (id && !dashboard) {
      navigate('/dashboards', { replace: true });
    }
  }, [id, dashboard, navigate]);

  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(dashboard?.name ?? '');
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [showCreatedToast, setShowCreatedToast] = useState(false);
  const toastConsumed = useRef(false);
  const addMenuRef = useRef<HTMLDivElement | null>(null);
  const debounceTimer = useRef<number | null>(null);

  useEffect(() => {
    if (toastConsumed.current) return;
    if (!justCreated) return;
    toastConsumed.current = true;
    setShowCreatedToast(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [justCreated, location.pathname, navigate]);

  useEffect(() => {
    if (!showCreatedToast) return;
    const timer = window.setTimeout(
      () => setShowCreatedToast(false),
      CREATED_TOAST_MS,
    );
    return () => window.clearTimeout(timer);
  }, [showCreatedToast]);

  useEffect(() => {
    if (!editingName) setDraftName(dashboard?.name ?? '');
  }, [dashboard?.name, editingName]);

  useEffect(() => {
    if (!addMenuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        addMenuRef.current &&
        !addMenuRef.current.contains(e.target as Node)
      ) {
        setAddMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [addMenuOpen]);

  useEffect(() => {
    return () => {
      if (debounceTimer.current !== null) {
        window.clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const layout = useMemo<LayoutItem[]>(() => {
    if (!dashboard) return [];
    return dashboard.widgets.map((w) => ({
      i: w.id,
      x: w.layout.x,
      y: w.layout.y,
      w: w.layout.w,
      h: w.layout.h,
    }));
  }, [dashboard]);

  const sortedWidgetIds = useMemo<string[]>(() => {
    if (!dashboard) return [];
    return [...dashboard.widgets]
      .sort((a, b) => {
        if (a.layout.y !== b.layout.y) return a.layout.y - b.layout.y;
        return a.layout.x - b.layout.x;
      })
      .map((w) => w.id);
  }, [dashboard]);

  const handleLayoutChange = useCallback(
    (next: readonly LayoutItem[]) => {
      if (!dashboard) return;
      const dashboardId = dashboard.id;
      if (debounceTimer.current !== null) {
        window.clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = window.setTimeout(() => {
        const map: Record<string, WidgetLayout> = {};
        for (const l of next) {
          map[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h };
        }
        updateLayouts(dashboardId, map);
        debounceTimer.current = null;
      }, LAYOUT_DEBOUNCE_MS);
    },
    [dashboard, updateLayouts],
  );

  if (!dashboard) {
    return null;
  }

  const commitRename = () => {
    const trimmed = draftName.trim();
    if (trimmed && trimmed !== dashboard.name) {
      renameDashboard(dashboard.id, trimmed);
    } else {
      setDraftName(dashboard.name);
    }
    setEditingName(false);
  };

  const handleAddWidget = (chartTypeId: string) => {
    addWidget(dashboard.id, chartTypeId);
    setAddMenuOpen(false);
  };

  const handleDelete = () => {
    if (confirm(`Delete dashboard "${dashboard.name}"?`)) {
      deleteDashboard(dashboard.id);
      navigate('/dashboards', { replace: true });
    }
  };

  return (
    <div className="dashboard-editor">
      {showCreatedToast && (
        <div
          className="dashboard-editor__toast"
          role="status"
          aria-live="polite"
        >
          <span>Created “{dashboard.name}”.</span>
          <button
            type="button"
            className="dashboard-editor__toast-dismiss"
            aria-label="Dismiss"
            onClick={() => setShowCreatedToast(false)}
          >
            ×
          </button>
        </div>
      )}
      <header className="dashboard-editor__header">
        <div className="dashboard-editor__title-row">
          <Link to="/dashboards" className="dashboard-editor__back">
            ← Dashboards
          </Link>
          {editingName ? (
            <input
              className="dashboard-editor__name-input"
              value={draftName}
              autoFocus
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') {
                  setDraftName(dashboard.name);
                  setEditingName(false);
                }
              }}
              aria-label="Dashboard name"
            />
          ) : (
            <button
              type="button"
              className="dashboard-editor__name"
              onClick={() => {
                setDraftName(dashboard.name);
                setEditingName(true);
              }}
              aria-label={`Rename dashboard ${dashboard.name}`}
            >
              {dashboard.name}
            </button>
          )}
        </div>
        <div className="dashboard-editor__actions">
          <div className="dashboard-editor__add" ref={addMenuRef}>
            <button
              type="button"
              className="dashboard-editor__action dashboard-editor__action--primary"
              onClick={() => setAddMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={addMenuOpen}
            >
              + Add widget
            </button>
            {addMenuOpen && (
              <ul className="dashboard-editor__add-menu" role="menu">
                {CHART_TYPES.map((ct) => (
                  <li key={ct.id} role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="dashboard-editor__add-menu-item"
                      onClick={() => handleAddWidget(ct.id)}
                    >
                      {ct.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className={`dashboard-editor__action${
              filterDrawerOpen ? ' dashboard-editor__action--active' : ''
            }`}
            onClick={() => setFilterDrawerOpen((v) => !v)}
            aria-pressed={filterDrawerOpen}
          >
            Edit filters
          </button>
          <button
            type="button"
            className="dashboard-editor__action dashboard-editor__action--danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        </div>
      </header>

      <div className="dashboard-editor__grid-wrap">
        {dashboard.widgets.length === 0 ? (
          <div className="dashboard-editor__empty" role="region" aria-label="Empty dashboard">
            <h2 className="dashboard-editor__empty-title">Add your first widget</h2>
            <p className="dashboard-editor__empty-desc">
              Pick a chart type to start visualising your filtered population.
            </p>
            <button
              type="button"
              className="dashboard-editor__action dashboard-editor__action--primary"
              onClick={() => setAddMenuOpen(true)}
            >
              + Add widget
            </button>
          </div>
        ) : (
          <SizedGridLayout
            className="dashboard-editor__grid"
            layout={layout}
            cols={GRID_COLS}
            rowHeight={GRID_ROW_HEIGHT}
            margin={[12, 12]}
            draggableHandle=".dashboard-widget__handle"
            onLayoutChange={handleLayoutChange}
          >
            {dashboard.widgets.map((w) => {
              const sortIdx = sortedWidgetIds.indexOf(w.id);
              return (
                <div key={w.id} className="dashboard-widget">
                  <DashboardWidget
                    dashboardId={dashboard.id}
                    widget={w}
                    canMoveUp={sortIdx > 0}
                    canMoveDown={
                      sortIdx >= 0 && sortIdx < sortedWidgetIds.length - 1
                    }
                  />
                </div>
              );
            })}
          </SizedGridLayout>
        )}
      </div>

      <DashboardFilterDrawer
        dashboardId={dashboard.id}
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      />
    </div>
  );
}
